import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import BottomNav from "../components/BottomNav";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Sparkles, Upload, ArrowRight, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

const CLASSIFY = {
  pending: "active",
  in_progress: "active",
  delivered: "active",
  disputed: "failed",
  admin_review: "failed",
  released: "completed",
  partially_released: "completed",
};

const TABS = [
  { k: "active", label: "Active", icon: Clock, tone: "text-nexus-blue" },
  { k: "completed", label: "Completed", icon: CheckCircle2, tone: "text-emerald-300" },
  { k: "failed", label: "Failed", icon: AlertTriangle, tone: "text-nexus-pink" },
];

const FAIL_REASONS = {
  disputed: "Dispute opened — awaiting resolution.",
  admin_review: "Escalated to Nexus admin review.",
};

export default function Collabs() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [hires, setHires] = useState([]);
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState("active");

  useEffect(() => {
    if (loading || !user) return;
    if (user.role !== "creator") { navigate("/brand"); return; }
    api.get("/hires")
      .then((r) => setHires(r.data))
      .catch(() => toast.error("Failed to load collabs"))
      .finally(() => setBusy(false));
  }, [user, loading, navigate]);

  const filtered = hires.filter((h) => CLASSIFY[h.status] === tab);

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback="/creator" label="Back to dashboard" />
      </div>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-4 pb-24 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-nexus-pink" /> Collabs
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">
            Your <span className="nexus-gradient-text">brand collabs</span>, end to end.
          </h1>
        </div>

        <div className="flex gap-2 flex-wrap" data-testid="collab-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count = hires.filter((h) => CLASSIFY[h.status] === t.k).length;
            const active = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`collab-tab-${t.k}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${active ? "nexus-gradient text-white border-transparent" : "bg-white/5 border-white/15 text-zinc-300 hover:bg-white/10"}`}
              >
                <Icon className={`h-3.5 w-3.5 ${active ? "text-white" : t.tone}`} />
                {t.label}
                <span className="text-[10px] opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        {busy && <div className="text-zinc-500 text-sm animate-pulse">Loading…</div>}
        {!busy && filtered.length === 0 && (
          <div className="glass rounded-2xl p-10 text-center text-sm text-zinc-500">
            No {tab} collabs yet.
          </div>
        )}
        <div className="space-y-3" data-testid="collabs-list">
          {filtered.map((h) => (
            <div key={h.id} className="glass rounded-2xl p-5 space-y-3" data-testid={`collab-item-${h.id}`}>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-lg truncate">{(h.package || {}).title || "Collab"}</div>
                  <div className="text-xs text-zinc-400 mt-1">{h.counterparty_name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Status · <span className="text-zinc-300">{h.status.replace(/_/g, " ")}</span></div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Payout</div>
                  <div className="font-display font-bold text-2xl nexus-gradient-text tabular-nums">£{(h.creator_payout || 0).toFixed(0)}</div>
                </div>
              </div>

              {tab === "failed" && (
                <div className="p-3 rounded-xl bg-nexus-pink/5 border border-nexus-pink/20 text-xs text-nexus-pink">
                  ⚠ {FAIL_REASONS[h.status] || "Deal ended without release."}
                  {h.dispute_reason && <span className="text-zinc-300"> · {h.dispute_reason}</span>}
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/deals/${h.id}`}
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-nexus-blue hover:text-white"
                  data-testid={`collab-view-briefing-${h.id}`}
                >
                  View briefing <ArrowRight className="h-3 w-3" />
                </Link>
                {tab === "active" && (h.status === "in_progress" || h.status === "delivered") && (
                  <Link
                    to={`/deals/${h.id}`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-nexus-pink hover:text-white"
                    data-testid={`collab-upload-${h.id}`}
                  >
                    <Upload className="h-3 w-3" /> {h.status === "delivered" ? "Update delivery" : "Upload content"}
                  </Link>
                )}
                <Link
                  to={`/messages/${h.id}`}
                  className="ml-auto text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
                  data-testid={`collab-message-${h.id}`}
                >
                  💬 Message brand
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
